import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { api } from './client';
import type { Paginated, RecipeSummary } from './types';

export type Store = {
  id: number;
  name: string;
  provider: string;
  priority: number;
  is_active: boolean;
  supports_cart: boolean;
  last_refreshed: string | null;
};

export type PlanEntry = {
  id: number;
  recipe: RecipeSummary;
  position: number;
  status: string;
  rationale: string;
  pairs_with: string[];
  cooked_on: string | null;
  /** Servings actually made, when that was not what the week planned for. */
  cooked_servings: number | null;
  /** Servings this dinner is *planned* for, when that is not the week's own
      figure. Optional rather than `number | null`: the query cache is
      offlineFirst, so a plan cached before this field existed has no key at
      all, and `entry.servings ?? plan.servings` is the only safe read. */
  servings?: number | null;
};

export type MealPlan = {
  /** Preferences were saved after this plan was chosen. */
  stale: boolean;
  id: number;
  week_start: string;
  store: Store;
  status: string;
  servings: number;
  model_used: string;
  generation_notes: string;
  /** Set when this store could not be planned at all, and why. */
  blocked_reason: string;
  /** When this week's shopping was put away, if it has been. A week that has
      been bought is settled: it cannot be re-chosen or swapped, only added
      to. Optional for the same offlineFirst reason as PlanEntry.servings, so
      always test it with Boolean() rather than against null. */
  shopped_at?: string | null;
  entries: PlanEntry[];
};

export type ListItem = {
  ingredient_id: number;
  name: string;
  category: string;
  quantity: string | null;
  quantity_display: string;
  unit: string;
  /** `string` only covers responses cached before ids were added. */
  recipes: ({ id: number; title: string } | string)[];
  /** How many packages to buy; null when it is bought loose or unknown. */
  buy_quantity: number | null;
  /** "2 × 16 oz", "1 orange", "about 1.6 lb" — empty when unknown. */
  buy_display: string;
  buy_is_estimate: boolean;
  buy_note: string;
  /** What the recipes call for, shown beside what to buy. */
  need_display: string;
  product_size: string;
  product_url: string;
  in_pantry: boolean;
  pantry_note: string;
  /** How much of the need the cupboard already covers, in the line's unit. */
  pantry_quantity: string | null;
  pantry_quantity_display: string;
  /** What is left to buy once the pantry has been counted. */
  outstanding_display: string;
  product_name: string;
  price: string | null;
  on_sale: boolean;
  product_id: string;
  /** The StoreProduct row, for rejecting a bad match from the cart screen. */
  match_id: number | null;
  substituted_from: string;
  unavailable: boolean;
  /** A better-matching product at this store that is out of stock, when that
      is why the line is buying something else. */
  passed_over_name: string;
  /** General ingredients this one purchase also answers for, because what is
      being bought is a kind of them. Optional because a list cached by the
      service worker before this existed is still a list worth rendering. */
  covers_names?: string[];
  is_knick_knack: boolean;
  can_make_at_home: boolean;
  /** Set on the spice lines a blend was broken into, naming the blend. Optional
      because a list cached by the service worker before this existed is still
      a list worth rendering. */
  homemade_for?: { ingredient_id: number; name: string } | null;
};

export type ShoppingList = {
  plan_id: number;
  store: string;
  store_id: number;
  supports_cart: boolean;
  servings: number;
  /** Whether some dinner on this week is planned for a different size, so the
      header does not report one number for a mixed list. */
  servings_vary?: boolean;
  /** When this week's shopping was put away into the pantry, if it has been. */
  shopped_at: string | null;
  departments: { category: string; label: string; items: ListItem[] }[];
  already_have: ListItem[];
  estimated_total: string | null;
  sale_savings: string | null;
};

export type Preferences = {
  servings: number;
  meals_per_week: number;
  liked_proteins: string[];
  disliked_proteins: string[];
  disliked_ingredients: number[];
  disliked_ingredient_names: string[];
  /** Each ruled-out ingredient, with the specific kinds it also covers. */
  disliked_ingredient_detail: {
    id: number;
    name: string;
    also_covers: string[];
  }[];
  dietary_tags: string[];
  variety_window_weeks: number;
  /** What each group covers, straight from the filter that expands them. */
  protein_groups: Record<string, string[]>;
  whole_foods_prime: boolean;
  whole_foods_employee_discount: boolean;
};

export type PantryItem = {
  id: number;
  ingredient: number;
  name: string;
  category: string;
  quantity: string | null;
  /** The amount as a kitchen says it ("1", "1 1/2") rather than "1.50000". */
  quantity_display: string;
  unit: string;
  note: string;
  purchased_date: string;
  opened_date: string | null;
  expiration_date: string | null;
  depleted: boolean;
  days_until_expiry: number | null;
  is_expiring_soon: boolean;
  /** Past its date: not stock any more, and the planner ignores it. */
  is_expired: boolean;
  /** In the freezer, and so on the freezer's shelf life rather than the fridge's. */
  frozen: boolean;
  frozen_on: string | null;
  /** Whether there is a freezer time on file for this at all. */
  freezable: boolean;
  /** Sealed, and dated by the packet rather than by any table we hold. */
  dated_by_packet: boolean;
};

export type PlanCosts = {
  store: string;
  total: string | null;
  priced_lines: number;
  unpriced_lines: number;
  recipes: { recipe_id: number; title: string; cost: string }[];
};

export function useCurrentPlans() {
  return useQuery({
    queryKey: ['plans', 'current'],
    queryFn: () => api<Paginated<MealPlan>>('/planner/plans/?week=current'),
  });
}


/** A week already eaten: the dinners and the reasoning, and no prices.
 *
 * Costs and the shopping list are computed from what the shops charge and
 * what the cupboard holds *today*, so quoting them against a finished week
 * would be inventing a number. The server leaves them out; so does the type.
 */
export type PastPlan = {
  id: number;
  week_start: string;
  /** Just the name -- a finished week is not a shop you can still choose. */
  store: string;
  status: string;
  servings: number;
  model_used: string;
  generation_notes: string;
  entries: PlanEntry[];
};

/** Both of these sit under ['plans'], which every plan mutation already
    invalidates. That matters at the week rollover more than it looks: the
    week accepted on Sunday becomes a past week on Monday, and the history
    that was empty a moment ago now has something in it. */
export function usePastPlans(page = 1) {
  return useQuery({
    queryKey: ['plans', 'history', page],
    queryFn: () => api<Paginated<PastPlan>>(`/planner/plans/history/?page=${page}`),
  });
}

export function usePastWeek(week: string | undefined) {
  return useQuery({
    queryKey: ['plans', 'history', week],
    enabled: Boolean(week),
    queryFn: () => api<Paginated<PastPlan>>(`/planner/plans/history/?week=${week}`),
  });
}

/** One plan's costs, as its own cache entry so the tabs and the open plan
    share whichever of them asked first. */
function planCostsQuery(planId: number | undefined) {
  return {
    queryKey: ['plan-costs', planId],
    queryFn: () => api<PlanCosts>(`/planner/plans/${planId}/costs/`),
    enabled: Boolean(planId),
  };
}

/** Costs are a second request: pricing a week means building its whole
    shopping list, which the plan itself has no reason to pay for. */
export function usePlanCosts(planId: number | undefined) {
  return useQuery(planCostsQuery(planId));
}

/** Every plan's weekly total, keyed by plan id.

    The store tabs price themselves, which means one request per store rather
    than one for the plan on screen. Same cache entries as usePlanCosts, so
    opening a store the tabs have already priced costs nothing further. */
export function usePlanCostTotals(planIds: number[]) {
  return useQueries({
    queries: planIds.map(planCostsQuery),
    combine: (results) =>
      Object.fromEntries(
        results.flatMap((result, index) =>
          result.data?.total ? [[planIds[index], result.data.total]] : [],
        ),
      ) as Record<number, string>,
  });
}

export function useGeneratePlans() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api<MealPlan[]>('/planner/plans/generate/', { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['plans'] }),
  });
}

export function useAcceptPlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (planId: number) =>
      api<MealPlan>(`/planner/plans/${planId}/accept/`, { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plans'] });
      queryClient.invalidateQueries({ queryKey: ['shopping-list'] });
    },
  });
}

/** Turn one dinner down, and say why.

    `reason` is the cook's own words, which is what the replacement is chosen
    against -- the score alone cannot tell that the objection was "too heavy"
    and would happily answer it with another cream sauce. Empty is fine.

    `neverAgain` blocks the recipe outright rather than just for this week, so
    it reaches past the plan into the corpus pages. */
export function useSwapEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      entryId,
      reason,
      neverAgain,
    }: {
      entryId: number;
      reason: string;
      neverAgain: boolean;
    }) =>
      api<MealPlan>(`/planner/entries/${entryId}/swap/`, {
        method: 'POST',
        body: { reason, never_again: neverAgain },
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['plans'] });
      // A block changes what the corpus pages say about that card, and how
      // many the Blocked filter counts. Only worth asking when one was made.
      if (variables.neverAgain) {
        queryClient.invalidateQueries({ queryKey: ['recipes'] });
        queryClient.invalidateQueries({ queryKey: ['recipe'] });
        queryClient.invalidateQueries({ queryKey: ['facets'] });
      }
    },
  });
}

/** Record a dinner as eaten.

    `servings` is what was actually made, which is usually what the week
    planned for and occasionally is not. Left out, the server uses the plan's
    own figure -- so the ordinary case sends nothing and cannot disagree with
    the plan by rounding it here. */
export function useMarkCooked() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ entryId, servings }: { entryId: number; servings?: number }) =>
      api<{ cooked: string; servings: number; pantry_items_used: number }>(
        `/planner/entries/${entryId}/cooked/`,
        { method: 'POST', body: servings === undefined ? undefined : { servings } },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plans'] });
      queryClient.invalidateQueries({ queryKey: ['pantry'] });
      // Cooking empties the cupboard, and the cupboard is half of what the
      // list is computed from.
      queryClient.invalidateQueries({ queryKey: ['shopping-list'] });
    },
  });
}

export function useAddRecipes() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      planId,
      count,
      servings,
      wants,
    }: {
      planId: number;
      count: number;
      servings: number;
      wants: string;
    }) =>
      api<MealPlan>(`/planner/plans/${planId}/add_recipes/`, {
        method: 'POST',
        body: { count, servings, wants },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plans'] });
      // A new dinner adds lines to the list and a row to the cost split.
      // ['plan-costs'] is a prefix, so it reaches every store tab's entry.
      queryClient.invalidateQueries({ queryKey: ['plan-costs'] });
      queryClient.invalidateQueries({ queryKey: ['shopping-list'] });
    },
  });
}

export function useSetEntryServings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ entryId, servings }: { entryId: number; servings: number }) =>
      api<MealPlan>(`/planner/entries/${entryId}/servings/`, {
        method: 'POST',
        body: { servings },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plans'] });
      // The list buys per dinner and the cost split charges per dinner, so
      // both are answers to a question this just changed. ['plan-costs'] is a
      // prefix, so it reaches the entry every store tab is holding.
      queryClient.invalidateQueries({ queryKey: ['shopping-list'] });
      queryClient.invalidateQueries({ queryKey: ['plan-costs'] });
    },
  });
}

/** What the cook has said about blends, as deltas against what the shop forces.
 *
 * A blend the shop cannot sell is mixed at home by default, so the two lists
 * are the exceptions: `home` adds one the shop does stock, `buy` takes back
 * one it does not. Sending a single whole set instead would make "nothing
 * ticked" and "the only default unticked" the same request.
 */
export type BlendOverrides = { home: number[]; buy: number[] };

export function blendQuery({ home, buy }: BlendOverrides): string {
  const params = new URLSearchParams();
  if (home.length) params.set('make_at_home', home.join(','));
  if (buy.length) params.set('buy_ready_made', buy.join(','));
  const query = params.toString();
  return query ? `?${query}` : '';
}

export function useShoppingList(
  planId: number | undefined,
  blends: BlendOverrides = { home: [], buy: [] },
) {
  return useQuery({
    queryKey: ['shopping-list', planId, blends],
    queryFn: () =>
      api<ShoppingList>(`/planner/plans/${planId}/shopping_list/${blendQuery(blends)}`),
    enabled: Boolean(planId),
  });
}

export function usePreferences() {
  return useQuery({
    queryKey: ['preferences'],
    queryFn: () => api<Preferences>('/planner/preferences/'),
  });
}

export function useSavePreferences() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<Preferences>) =>
      api<Preferences>('/planner/preferences/', { method: 'POST', body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['preferences'] });
      queryClient.invalidateQueries({ queryKey: ['plans'] });
      queryClient.invalidateQueries({ queryKey: ['shopping-list'] });
    },
  });
}

export function usePantry() {
  return useQuery({
    queryKey: ['pantry'],
    queryFn: () => api<PantryItem[]>('/pantry/'),
  });
}

export type PantryVerb = 'open' | 'deplete' | 'freeze' | 'thaw' | 'expiry';

/** One verb applied to one or more pantry rows. `expirationDate` is for
    `expiry` only. */
export function usePantryAction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      ids,
      verb,
      expirationDate,
    }: {
      ids: number[];
      verb: PantryVerb;
      expirationDate?: string;
    }) =>
      Promise.all(
        ids.map((id) =>
          api<PantryItem>(`/pantry/${id}/${verb}/`, {
            method: 'POST',
            body: verb === 'expiry' ? { expiration_date: expirationDate } : undefined,
          }),
        ),
      ),
    // Settled rather than succeeded: a bulk action can fail partway, and the
    // rows it did reach have still changed.
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['pantry'] });
      // Freezing changes what the cupboard covers and for how long, which is
      // half of what the list is computed from.
      queryClient.invalidateQueries({ queryKey: ['shopping-list'] });
    },
  });
}

export function useAddToCart() {
  return useMutation({
    mutationFn: ({ storeId, items }: { storeId: number; items: unknown[] }) =>
      api<{ added: string[]; failed: string[]; checkout_url: string }>(
        `/stores/${storeId}/cart/`,
        { method: 'POST', body: { items } },
      ),
  });
}


export type IngredientOption = {
  id: number;
  name: string;
  category: string;
  /** What the corpus measures it in — the pantry seeds its unit box from it. */
  default_unit: string;
  recipes: number;
};

/** Search canonical ingredients, commonest first.

    Backs both ingredient pickers. `excludeArtifacts` drops the rows that were
    never food — extraction put "vegetarian" in the ingredient table, and it is
    a plausible thing to rule out and an impossible thing to have in a cupboard. */
export function useIngredientSearch(
  term: string,
  { excludeArtifacts = false }: { excludeArtifacts?: boolean } = {},
) {
  const query = new URLSearchParams();
  if (term) query.set('search', term);
  if (excludeArtifacts) query.set('exclude_artifacts', 'true');
  const suffix = query.toString();

  return useQuery({
    queryKey: ['ingredient-search', term, excludeArtifacts],
    queryFn: () => api<IngredientOption[]>(`/recipes/ingredients/${suffix ? `?${suffix}` : ''}`),
    enabled: term.trim().length >= 2,
  });
}

export type PantryForm = {
  id: number;
  ingredient: number;
  ingredient_name: string;
  substitute: number;
  substitute_name: string;
  per_unit: string;
  ratio_quantity: string;
  ratio_unit: string;
  preferred: boolean;
  note: string;
};

/** How this kitchen keeps things — jarred garlic rather than cloves.

    Only the shopping list reads these. The card still says "4 cloves". */
export function usePantryForms() {
  return useQuery({
    queryKey: ['pantry-forms'],
    queryFn: () => api<PantryForm[]>('/pantry/forms/'),
  });
}

export function useSetPantryForm() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, preferred }: { id: number; preferred: boolean }) =>
      api<PantryForm>(`/pantry/forms/${id}/`, {
        method: 'PATCH',
        body: { preferred },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pantry-forms'] });
      // The shopping list is built from these, so it is now wrong.
      queryClient.invalidateQueries({ queryKey: ['shopping-list'] });
    },
  });
}

/** What is already in the cupboard, as the shopping list reports it.

    Dates are how this differs from a shop. A jar noticed while reading the
    list was bought weeks ago, so its expiry counts from then — and from when
    it was opened, if it has been. A printed use-by overrides both. */
export type StockRow = {
  ingredient_id: number;
  note?: string;
  quantity?: string;
  unit?: string;
  purchased_date?: string;
  opened_date?: string;
  expiration_date?: string;
  /** Straight into the freezer, on the freezer's clock. */
  frozen?: boolean;
};

/** Put something straight into the pantry.

    Used by the cart screen's "already have it": a one-off exclusion would need
    making again every week, whereas the pantry is consulted every time the
    list is built. */
export function useStockPantry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (row: StockRow) =>
      api<PantryItem[]>('/pantry/restock/', {
        method: 'POST',
        body: { items: [row] },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pantry'] });
      queryClient.invalidateQueries({ queryKey: ['shopping-list'] });
    },
  });
}

/** Put a finished shop away: what was bought becomes pantry stock.

    The ids are the ticked lines — what actually went in the basket, as
    against what the list asked for. What lands in the pantry is the package
    rather than the portion the recipes wanted, so the leftover is there for
    next week's plan to be built around. */
export function useMarkShopped() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      planId,
      ingredientIds,
      again,
      blends,
    }: {
      planId: number;
      ingredientIds?: number[];
      /** A second shop for the same week, after dinners were added to it.
          The server refuses one without this, so that a page reopened in the
          aisle cannot quietly stock the cupboard twice. */
      again?: boolean;
      /** The list's own blend overrides. What goes into the cupboard has to
          be what the list said to buy: a week that mixed its own spice blend
          puts the spices away, not a jar it never bought. */
      blends?: BlendOverrides;
    }) =>
      api<{ stocked: number; shopped_at: string }>(
        `/planner/plans/${planId}/shopped/`,
        {
          method: 'POST',
          body: {
            ingredient_ids: ingredientIds,
            again,
            // Only the exceptions, the way `again` and `ingredient_ids` are:
            // the blends this shop cannot sell are mixed at home whether or
            // not the request says so, and a body full of empty lists says
            // the cook made a choice they did not make.
            ...(blends?.home.length ? { make_at_home: blends.home } : {}),
            ...(blends?.buy.length ? { buy_ready_made: blends.buy } : {}),
          },
        },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pantry'] });
      // Now partly covered by the cupboard, so the list itself has changed.
      queryClient.invalidateQueries({ queryKey: ['shopping-list'] });
      // shopped_at is on the plan too, and it is what settles the plan page.
      queryClient.invalidateQueries({ queryKey: ['plans'] });
    },
  });
}

export type StoreLink = {
  id: number;
  name: string;
  provider: string;
  supports_cart: boolean;
  cart_linked: boolean;
  cart_linked_at: string | null;
  last_error: string;
};

export function useStores() {
  return useQuery({
    queryKey: ['stores'],
    queryFn: () => api<StoreLink[]>('/stores/'),
  });
}

/** Start the store's authorization flow.

    The URL comes back as JSON for the page to navigate to, rather than as a
    302 — `fetch` would follow a cross-origin redirect it can do nothing
    useful with. */
export function useConnectStore() {
  return useMutation({
    mutationFn: (storeId: number) =>
      api<{ authorize_url: string }>(`/stores/${storeId}/authorize/`, {
        method: 'POST',
      }),
    onSuccess: (data) => {
      window.location.href = data.authorize_url;
    },
  });
}

export function useDisconnectStore() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (storeId: number) =>
      api<{ linked: boolean }>(`/stores/${storeId}/unlink/`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['stores'] }),
  });
}
